<template>
    <v-dialog v-model="internalDialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="text-h5">
                Edit Goal
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-subtitle class="pb-0">
                Update goal details and track progress
            </v-card-subtitle>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <!-- Goal Name -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Goal Name <span class="red--text">*</span>
                        </label>
                        <v-text-field 
                            v-model="formData.name" 
                            placeholder="e.g., Q4 Marketing Campaign" 
                            outlined 
                            dense
                            :rules="[rules.required, rules.maxLength]"
                        ></v-text-field>
                    </div>

                    <!-- Description -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Description
                        </label>
                        <v-textarea 
                            v-model="formData.description"
                            placeholder="Describe the goal and its objectives..." 
                            outlined 
                            dense 
                            rows="3"
                            auto-grow
                        ></v-textarea>
                    </div>

                    <!-- Goal Type -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Goal Type
                        </label>
                        <v-radio-group v-model="formData.goal_type" row class="mt-0">
                            <v-radio label="New Website" value="new_website"></v-radio>
                            <v-radio label="New Business Unit" value="new_business_unit"></v-radio>
                        </v-radio-group>
                    </div>

                    <!-- Status and Phase Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Status
                                </label>
                                <v-select 
                                    v-model="formData.status" 
                                    :items="statusOptions" 
                                    placeholder="Planning" 
                                    outlined
                                    dense
                                ></v-select>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Phase
                                </label>
                                <v-text-field 
                                    v-model="formData.phase" 
                                    placeholder="e.g., Discovery, Development" 
                                    outlined 
                                    dense
                                    :rules="[rules.maxLength]"
                                ></v-text-field>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Date Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Start Date
                                </label>
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formData.start_date"
                                            placeholder="Pick a date"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.start_date"
                                        @input="startDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Deadline
                                </label>
                                <v-menu
                                    v-model="deadlineMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formData.deadline"
                                            placeholder="Pick a date"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.deadline"
                                        @input="deadlineMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Assigned To (Multiple) -->
                    <!-- <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Assigned To (Multiple)
                        </label>
                        <v-autocomplete 
                            v-model="formData.assignees" 
                            :items="usersList" 
                            item-text="name" 
                            item-value="id"
                            placeholder="Select people" 
                            outlined 
                            dense 
                            multiple 
                            chips 
                            small-chips 
                            deletable-chips
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 3" small close @click:close="removeAssignee(item.id)">
                                    {{ item.name }}
                                </v-chip>
                                <span v-if="index === 3" class="grey--text text-caption">
                                    (+{{ formData.assignees.length - 3 }} others)
                                </span>
                            </template>
                        </v-autocomplete>
                    </div> -->

                    <!-- Progress Info (Read-only, auto-calculated) -->
                    <v-alert type="info" dense text class="mb-4">
                        Progress is automatically calculated based on milestone completion:
                        <strong>{{ calculateGoalProgress() }}%</strong> complete
                    </v-alert>
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-btn text color="error" @click="confirmDelete" :disabled="loading">
                    <v-icon left>mdi-delete</v-icon>
                    Delete
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="updateGoal" :loading="loading" :disabled="!valid">
                    Update Goal
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Delete Confirmation Dialog -->
        <v-dialog v-model="deleteDialog" max-width="500">
            <v-card>
                <v-card-title class="text-h6">
                    Delete Goal?
                </v-card-title>
                <v-card-text>
                    Are you sure you want to delete "{{ goal && goal.name ? goal.name : 'this goal' }}"? This action cannot be undone.
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="deleteDialog = false">
                        Cancel
                    </v-btn>
                    <v-btn color="error" text @click="deleteGoal" :loading="deleting">
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
export default {
    name: 'EditGoalDialog',
    inject: ['users', 'optimisticUpdateGoal', 'optimisticDeleteGoal'],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        goal: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            internalDialog: this.value,
            valid: false,
            loading: false,
            deleting: false,
            deleteDialog: false,
            startDateMenu: false,
            deadlineMenu: false,
            formData: {
                name: '',
                description: '',
                goal_type: 'new_website',
                status: 'Planning',
                phase: '',
                start_date: null,
                deadline: null,
                assignees: []
            },
            statusOptions: ['Planning', 'Active', 'On Hold', 'Completed'],
            rules: {
                required: (v) => !!v || 'This field is required',
                maxLength: (v) => !v || v.length <= 255 || 'Maximum 255 characters'
            }
        };
    },
    computed: {
        usersList() {
            return this.users() || [];
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
            if (val) {
                this.populateForm();
            }
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.resetForm();
            }
        },
        goal: {
            deep: true,
            handler() {
                if (this.internalDialog) {
                    this.populateForm();
                }
            }
        }
    },
    methods: {
        closeDialog() {
            this.internalDialog = false;
        },
        populateForm() {
            if (this.goal && this.goal.id) {
                // Extract assignees - handle if it's array of IDs or array of objects
                let assigneeIds = [];
                if (this.goal.assignees && Array.isArray(this.goal.assignees)) {
                    assigneeIds = this.goal.assignees.map(assignee => {
                        return typeof assignee === 'object' ? assignee.user_id || assignee.id : assignee;
                    }).filter(id => id);
                }

                // Format dates from timestamps to YYYY-MM-DD format for v-date-picker
                const formatDate = (date) => {
                    if (!date) return null;
                    // If it's already in YYYY-MM-DD format, return as is
                    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
                        return date;
                    }
                    // Parse timestamp and format
                    const dateObj = this.$date(date);
                    return dateObj.isValid() ? dateObj.format('YYYY-MM-DD') : null;
                };

                this.formData = {
                    name: this.goal.name || '',
                    description: this.goal.description || '',
                    goal_type: this.goal.goal_type || 'new_website',
                    status: this.goal.status || 'Planning',
                    phase: this.goal.phase || '',
                    start_date: formatDate(this.goal.start_date),
                    deadline: formatDate(this.goal.deadline),
                    assignees: assigneeIds
                };
            }
        },
        removeAssignee(userId) {
            const index = this.formData.assignees.indexOf(userId);
            if (index > -1) {
                this.formData.assignees.splice(index, 1);
            }
        },
        calculateGoalProgress() {
            // This would need to be implemented similar to ViewCompanyComponent
            // For now, return a placeholder
            return this.goal?.completion_percentage || 0;
        },
        resetForm() {
            this.formData = {
                name: '',
                description: '',
                goal_type: 'new_website',
                status: 'Planning',
                phase: '',
                start_date: null,
                deadline: null,
                assignees: []
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        async updateGoal() {
            if (!this.$refs.form.validate()) {
                return;
            }

            if (!this.goal || !this.goal.id) {
                this.$toast.error('No goal selected');
                return;
            }

            this.loading = true;
            try {
                await this.optimisticUpdateGoal(
                    this.goal.id,
                    this.formData,
                    async () => {
                        const response = await this.$axios.post(`api/goals-v2/${this.goal.id}`, this.formData);
                        
                        if (!response.data.response) {
                            throw new Error(response.data.message || 'Failed to update goal');
                        }
                        
                        return response.data;
                    }
                );

                this.$emit('goal-updated');
                this.$toast.success('Goal updated successfully');
                this.closeDialog();
            } catch (error) {
                console.error('Error updating goal:', error);
                this.$toast.error(error.message || 'An error occurred while updating the goal');
            } finally {
                this.loading = false;
            }
        },
        confirmDelete() {
            this.deleteDialog = true;
        },
        async deleteGoal() {
            if (!this.goal || !this.goal.id) {
                this.$toast.error('No goal selected');
                return;
            }

            this.deleting = true;
            try {
                await this.optimisticDeleteGoal(
                    this.goal.id,
                    async () => {
                        const response = await this.$axios.delete(`api/goals-v2/${this.goal.id}`);
                        
                        if (!response.data.response) {
                            throw new Error(response.data.message || 'Failed to delete goal');
                        }
                        
                        return response.data;
                    }
                );

                this.$emit('goal-deleted');
                this.$toast.success('Goal deleted successfully');
                this.deleteDialog = false;
                this.closeDialog();
            } catch (error) {
                console.error('Error deleting goal:', error);
                this.$toast.error(error.message || 'An error occurred while deleting the goal');
            } finally {
                this.deleting = false;
            }
        }
    }
};
</script>

<style scoped>
.v-card-title {
    background-color: #f5f5f5;
}

label {
    color: #333;
}
</style>
